// ---------------------------------------------------------------------------
// PrimeBroker.js — Clear Street SFTP Data (Warm Stone Design)
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import theme from "../components/theme";
import {
  Card,
  PageHeader,
  StatCard,
  Badge,
  Button,
  LoadingSpinner,
  EmptyState,
} from "../components/ui";

const t = theme;

// —— Helpers ——————————————————————————————————————————————————
const fmt = (v, decimals = 2) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const fmtCur = (v, ccy = "USD") => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  const prefix = ccy === "SEK" ? "kr" : "$";
  const sign = n < 0 ? "-" : "";
  return `${sign}${prefix}${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtQty = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
};

// —— Themed table styles ——————————————————————————————————————
const thStyle = (align = "left") => ({
  textAlign: align,
  padding: "9px 14px",
  borderBottom: `2px solid ${t.border}`,
  background: t.tableHeaderBg,
  color: t.textMuted,
  fontWeight: 600,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  fontFamily: t.fontBody,
});

const tdStyle = (align = "left", mono = false) => ({
  textAlign: align,
  padding: "9px 14px",
  borderBottom: `1px solid ${t.borderLight}`,
  color: t.text,
  fontSize: 12,
  fontFamily: mono ? t.fontMono : t.fontBody,
  fontVariantNumeric: mono ? "tabular-nums" : "normal",
});

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function PrimeBroker() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("positions");
  const [dateFilter, setDateFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");

  const fetchOverview = useCallback(async (targetDate) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (targetDate) params.date = targetDate;
      const res = await api.get("/integrations/clearstreet/sftp/overview/", { params });
      setOverview(res.data);
      if (res.data.as_of && !targetDate) {
        setDateFilter(res.data.as_of);
      }
    } catch (err) {
      console.error("CS SFTP overview error:", err);
      setError(
        err.response?.status === 401
          ? "Authentication failed. Please log in again."
          : "Failed to load Clear Street data. Check that SFTP sync has run."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setDateFilter(d);
    if (d) fetchOverview(d);
  };

  // —— Derived data ———————————————————————————————————————————
  const positions = overview?.positions || [];
  const trades = overview?.trades || [];
  const summaryRows = overview?.summary || [];
  const settlements = overview?.settlements || [];

  // Unique accounts for filter
  const accountIds = [...new Set(positions.map((p) => p.account_id).filter(Boolean))];

  // Apply account filter
  const filterByAccount = (rows) =>
    accountFilter ? rows.filter((r) => String(r.account_id) === accountFilter) : rows;

  // Separate equity from cash
  const equityPositions = filterByAccount(
    positions.filter((p) => p.instrument_type && p.instrument_type.toLowerCase() !== "cash")
  );
  const cashPositions = filterByAccount(
    positions.filter((p) => !p.instrument_type || p.instrument_type.toLowerCase() === "cash")
  );

  // ★ FIX: Compute totals PER CURRENCY from positions (not from backend totals)
  const computeTotals = (posArr) => {
    const result = { USD: { long: 0, short: 0, cash: 0 }, SEK: { long: 0, short: 0, cash: 0 } };
    posArr.forEach((p) => {
      const ccy = (p.currency || "USD").toUpperCase();
      if (!result[ccy]) result[ccy] = { long: 0, short: 0, cash: 0 };
      const isCash = !p.instrument_type || p.instrument_type.toLowerCase() === "cash";
      if (isCash) {
        result[ccy].cash += parseFloat(p.td_long_market_value || 0);
      } else {
        result[ccy].long += parseFloat(p.td_long_market_value || 0);
        result[ccy].short += parseFloat(p.td_short_market_value || 0);
      }
    });
    // Compute NAV per currency
    Object.keys(result).forEach((ccy) => {
      const t = result[ccy];
      t.nav = t.long + t.short + t.cash;
    });
    return result;
  };

  const currTotals = computeTotals(positions);
  const usd = currTotals.USD || { long: 0, short: 0, cash: 0, nav: 0 };
  const sek = currTotals.SEK || { long: 0, short: 0, cash: 0, nav: 0 };
  const hasSEK = sek.nav !== 0;

  // Tab counts
  const filteredTrades = filterByAccount(trades);
  const filteredSummary = filterByAccount(summaryRows);
  const filteredSettlements = filterByAccount(settlements);

  // —— Render ————————————————————————————————————————————————
  if (loading && !overview) return <LoadingSpinner message="Loading Clear Street data..." />;

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Prime Broker — Clear Street"
        subtitle={
          <>
            SFTP data {overview?.as_of ? `as of ${overview.as_of}` : ""}{" "}
            {overview?.earliest_date && <span>· History from {overview.earliest_date}</span>}
          </>
        }
        action={
          <Badge
            text={overview?.data_available ? "● CONNECTED" : "○ NO DATA"}
            variant={overview?.data_available ? "green" : "red"}
          />
        }
      />

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: `1px solid ${t.danger}20`,
            borderRadius: t.radius,
            padding: "12px 16px",
            color: t.danger,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* ★ FIX: KPI Cards — USD totals (primary) */}
      {overview?.data_available && (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: hasSEK ? 8 : 16, flexWrap: "wrap" }}>
            <StatCard label="Net Asset Value (USD)" value={fmtCur(usd.nav)} />
            <StatCard label="Long Market Value (USD)" value={fmtCur(usd.long)} color={t.success} />
            <StatCard label="Short Market Value (USD)" value={fmtCur(usd.short)} color={t.danger} />
            <StatCard label="Cash Balance (USD)" value={fmtCur(usd.cash)} color={t.info} />
          </div>

          {/* SEK totals (secondary row, only if SEK positions exist) */}
          {hasSEK && (() => {
            const sekRate = parseFloat(overview?.totals?.fx_rates?.SEK || 0);
            const sekNavUsd = sekRate > 0 ? sek.nav * sekRate : null;
            return (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <StatCard label="Net Asset Value (SEK)" value={fmtCur(sek.nav, "SEK")} />
              <StatCard label="Long Market Value (SEK)" value={fmtCur(sek.long, "SEK")} color={t.success} />
              <StatCard label="Short Market Value (SEK)" value={fmtCur(sek.short, "SEK")} color={t.danger} />
              <StatCard label="Cash Balance (SEK)" value={fmtCur(sek.cash, "SEK")} color={t.info} />
              {sekNavUsd != null && <StatCard label="SEK NAV in USD" value={fmtCur(sekNavUsd, "USD")} color={t.textMuted} />}
            </div>
            );
          })()}
        </>
      )}

      {/* Controls Row */}
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 4 }}>
            Date
          </div>
          <input
            type="date"
            value={dateFilter}
            onChange={handleDateChange}
            style={{
              padding: "7px 10px",
              border: `1px solid ${t.border}`,
              borderRadius: t.radiusSm,
              fontSize: 13,
              color: t.text,
              background: t.bgCard,
              fontFamily: t.fontBody,
            }}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 4 }}>
            Account
          </div>
          <select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            style={{
              padding: "7px 10px",
              border: `1px solid ${t.border}`,
              borderRadius: t.radiusSm,
              fontSize: 13,
              color: t.text,
              background: t.bgCard,
              fontFamily: t.fontBody,
            }}
          >
            <option value="">All Accounts</option>
            {accountIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </div>
        {loading && (
          <span style={{ fontSize: 12, color: t.textMuted, paddingBottom: 8 }}>
            Refreshing...
          </span>
        )}
      </div>

      {/* Tab Bar */}
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: `2px solid ${t.border}`,
          marginBottom: 20,
        }}
      >
        {[
          { key: "positions", label: `Positions (${equityPositions.length + cashPositions.length})` },
          { key: "trades", label: `Trades (${filteredTrades.length})` },
          { key: "summary", label: `Summary (${filteredSummary.length})` },
          { key: "settlements", label: `Settlements (${filteredSettlements.length})` },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            style={{
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              color: tab === item.key ? t.accent : t.textMuted,
              borderBottom:
                tab === item.key ? `2px solid ${t.accent}` : "2px solid transparent",
              marginBottom: -2,
              transition: t.transitionFast,
              fontFamily: t.fontBody,
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === "positions" && (
        <PositionsTab equities={equityPositions} cash={cashPositions} fxRates={overview?.totals?.fx_rates || {}} />
      )}
      {tab === "trades" && <TradesTab trades={filteredTrades} />}
      {tab === "summary" && <SummaryTab rows={filteredSummary} />}
      {tab === "settlements" && <SettlementsTab rows={filteredSettlements} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: POSITIONS
// ═══════════════════════════════════════════════════════════════
function PositionsTab({ equities, cash, fxRates }) {
  const hasNonUSD = cash.some(p => (p.currency || 'USD').toUpperCase() !== 'USD');
  return (
    <>
      {/* Equity Positions */}
      <Card title="Equity Positions" noPad style={{ marginBottom: 20 }}>
        {equities.length === 0 ? (
          <EmptyState message="No equity positions for this date." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle()}>Instrument</th>
                  <th style={thStyle()}>Type</th>
                  <th style={thStyle()}>CCY</th>
                  <th style={thStyle()}>Account</th>
                  <th style={thStyle("right")}>Quantity (TD)</th>
                  <th style={thStyle("right")}>Price</th>
                  <th style={thStyle("right")}>Long MV</th>
                  <th style={thStyle("right")}>Short MV</th>
                  <th style={thStyle("right")}>Margin %</th>
                  <th style={thStyle("right")}>Margin Amt</th>
                </tr>
              </thead>
              <tbody>
                {equities.map((p, i) => (
                  <tr
                    key={p.id || i}
                    style={{ transition: t.transitionFast }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ ...tdStyle(), fontWeight: 600 }}>{p.instrument_name}</td>
                    <td style={tdStyle()}>
                      <Badge text={p.instrument_type || "—"} variant="blue" />
                    </td>
                    <td style={tdStyle()}>
                      <Badge text={p.currency || "USD"} variant={p.currency === "SEK" ? "amber" : "default"} />
                    </td>
                    <td style={{ ...tdStyle(), fontSize: 11, color: t.textMuted }}>
                      {p.account_number || p.account_id}
                    </td>
                    <td style={tdStyle("right", true)}>{fmtQty(p.today_td_quantity)}</td>
                    <td style={tdStyle("right", true)}>{fmt(p.closing_price, 4)}</td>
                    <td style={tdStyle("right", true)}>
                      {fmtCur(p.td_long_market_value, p.currency)}
                    </td>
                    <td style={tdStyle("right", true)}>
                      {fmtCur(p.td_short_market_value, p.currency)}
                    </td>
                    <td style={tdStyle("right", true)}>{fmt(p.margin_percent, 1)}%</td>
                    <td style={tdStyle("right", true)}>
                      {fmtCur(p.margin_amount, p.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Cash Balances */}
      <Card title="Cash Balances" noPad>
        {cash.length === 0 ? (
          <EmptyState message="No cash positions for this date." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle()}>Currency</th>
                  <th style={thStyle()}>Account</th>
                  <th style={thStyle("right")}>TD Balance</th>
                  <th style={thStyle("right")}>SD Balance</th>
                  {hasNonUSD && <th style={thStyle("right")}>USD Equivalent</th>}
                </tr>
              </thead>
              <tbody>
                {cash.map((p, i) => {
                  const ccy = (p.currency || 'USD').toUpperCase();
                  const rate = ccy === 'USD' ? 1 : parseFloat(fxRates[ccy] || 0);
                  const tdBal = parseFloat(p.td_long_market_value || 0);
                  const usdEquiv = ccy !== 'USD' && rate > 0 ? tdBal * rate : null;
                  return (
                  <tr
                    key={p.id || i}
                    style={{ transition: t.transitionFast }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ ...tdStyle(), fontWeight: 600 }}>{p.currency}</td>
                    <td style={{ ...tdStyle(), fontSize: 11, color: t.textMuted }}>
                      {p.account_number || p.account_id}
                    </td>
                    <td style={tdStyle("right", true)}>
                      {fmtCur(p.td_long_market_value, p.currency)}
                    </td>
                    <td style={tdStyle("right", true)}>
                      {fmtCur(p.sd_long_market_value, p.currency)}
                    </td>
                    {hasNonUSD && (
                      <td style={{ ...tdStyle("right", true), color: usdEquiv != null ? t.info : t.textMuted }}>
                        {usdEquiv != null ? fmtCur(usdEquiv, 'USD') : '—'}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: TRADES
// ═══════════════════════════════════════════════════════════════
function TradesTab({ trades }) {
  if (trades.length === 0) {
    return (
      <Card noPad>
        <EmptyState message="No trades for this date (may be a non-trading day)." />
      </Card>
    );
  }

  return (
    <Card noPad>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thStyle()}>Trade Date</th>
              <th style={thStyle()}>Settle</th>
              <th style={thStyle()}>Side</th>
              <th style={thStyle()}>Instrument</th>
              <th style={thStyle()}>Type</th>
              <th style={thStyle("right")}>Qty</th>
              <th style={thStyle("right")}>Price</th>
              <th style={thStyle("right")}>Gross</th>
              <th style={thStyle("right")}>Fees</th>
              <th style={thStyle("right")}>Net</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((tr, i) => {
              const totalFees =
                parseFloat(tr.fee_commission || 0) +
                parseFloat(tr.fee_sec || 0) +
                parseFloat(tr.fee_taf || 0) +
                parseFloat(tr.fee_orf || 0);
              const isBuy = (tr.side || "").toLowerCase().includes("buy");

              return (
                <tr
                  key={tr.id || i}
                  style={{ transition: t.transitionFast }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={tdStyle()}>{tr.trade_date || "—"}</td>
                  <td style={tdStyle()}>{tr.settle_date || "—"}</td>
                  <td style={tdStyle()}>
                    <Badge
                      text={(tr.side || tr.action || "—").toUpperCase()}
                      variant={isBuy ? "green" : "red"}
                    />
                  </td>
                  <td style={{ ...tdStyle(), fontWeight: 600 }}>{tr.instrument_name}</td>
                  <td style={tdStyle()}>{tr.instrument_type || "—"}</td>
                  <td style={tdStyle("right", true)}>{fmtQty(tr.quantity)}</td>
                  <td style={tdStyle("right", true)}>{fmt(tr.price, 4)}</td>
                  <td style={tdStyle("right", true)}>{fmtCur(tr.gross_amount, tr.currency)}</td>
                  <td style={tdStyle("right", true)}>{fmtCur(totalFees, tr.currency)}</td>
                  <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                    {fmtCur(tr.net_amount, tr.currency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: SUMMARY
// ═══════════════════════════════════════════════════════════════
function SummaryTab({ rows }) {
  if (rows.length === 0) {
    return (
      <Card noPad>
        <EmptyState message="No summary data for this date." />
      </Card>
    );
  }

  return (
    <div style={{ display: "grid", gap: 20 }}>
      {rows.map((s) => {
        const ccy = s.currency || "USD";
        const tdLong =
          parseFloat(s.td_equity_long_mv || 0) +
          parseFloat(s.td_option_long_mv || 0) +
          parseFloat(s.td_fixed_income_long_mv || 0);
        const tdShort =
          parseFloat(s.td_equity_short_mv || 0) +
          parseFloat(s.td_option_short_mv || 0) +
          parseFloat(s.td_fixed_income_short_mv || 0);
        const tdCash = parseFloat(s.td_cash || 0);
        const tdNet = tdLong + tdShort + tdCash;

        return (
          <Card key={s.id} noPad>
            <div
              style={{
                padding: "14px 20px",
                borderBottom: `1px solid ${t.borderLight}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: t.text }}>
                  {s.account_full_name || `Account ${s.account_id}`}
                </span>
                <Badge text={ccy} variant={ccy === "SEK" ? "amber" : "blue"} />
              </div>
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily: t.fontMono,
                  color: t.text,
                }}
              >
                {fmtCur(tdNet, ccy)}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={thStyle()}>Category</th>
                    <th style={thStyle("right")}>Trade Date Value</th>
                    <th style={thStyle("right")}>Settle Date Value</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Equity — Long", s.td_equity_long_mv, s.sd_equity_long_mv],
                    ["Equity — Short", s.td_equity_short_mv, s.sd_equity_short_mv],
                    ["Options — Long", s.td_option_long_mv, s.sd_option_long_mv],
                    ["Options — Short", s.td_option_short_mv, s.sd_option_short_mv],
                    ["Fixed Income — Long", s.td_fixed_income_long_mv, s.sd_fixed_income_long_mv],
                    ["Fixed Income — Short", s.td_fixed_income_short_mv, s.sd_fixed_income_short_mv],
                  ].map(([label, td, sd], i) => (
                    <tr
                      key={label}
                      style={{ transition: t.transitionFast }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ ...tdStyle(), fontWeight: 600 }}>{label}</td>
                      <td style={tdStyle("right", true)}>{fmtCur(td, ccy)}</td>
                      <td style={tdStyle("right", true)}>{fmtCur(sd, ccy)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: t.bgSubtle }}>
                    <td style={{ ...tdStyle(), fontWeight: 700 }}>Cash</td>
                    <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                      {fmtCur(s.td_cash, ccy)}
                    </td>
                    <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                      {fmtCur(s.sd_cash, ccy)}
                    </td>
                  </tr>
                  <tr style={{ background: t.bgSubtle }}>
                    <td style={{ ...tdStyle(), fontWeight: 700 }}>Netted Cash</td>
                    <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                      {fmtCur(s.td_netted_cash, ccy)}
                    </td>
                    <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                      {fmtCur(s.sd_netted_cash, ccy)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: SETTLEMENTS
// ═══════════════════════════════════════════════════════════════
function SettlementsTab({ rows }) {
  if (rows.length === 0) {
    return (
      <Card noPad>
        <EmptyState message="No settlement activity for this date." />
      </Card>
    );
  }

  return (
    <Card noPad>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thStyle()}>Settle Date</th>
              <th style={thStyle()}>Trade Date</th>
              <th style={thStyle()}>Description</th>
              <th style={thStyle()}>Symbol</th>
              <th style={thStyle()}>Code</th>
              <th style={thStyle("right")}>Qty</th>
              <th style={thStyle("right")}>Price</th>
              <th style={thStyle("right")}>Gross</th>
              <th style={thStyle("right")}>Fees</th>
              <th style={thStyle("right")}>Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id || i}
                style={{ transition: t.transitionFast }}
                onMouseEnter={(e) => (e.currentTarget.style.background = t.tableRowHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={tdStyle()}>{r.settle_date || "—"}</td>
                <td style={tdStyle()}>{r.trade_date || "—"}</td>
                <td
                  style={{
                    ...tdStyle(),
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.description || r.memo || "—"}
                </td>
                <td style={{ ...tdStyle(), fontWeight: 600 }}>{r.symbol || "—"}</td>
                <td style={tdStyle()}>
                  <Badge text={r.transaction_code || "—"} variant="default" />
                </td>
                <td style={tdStyle("right", true)}>{fmtQty(r.quantity)}</td>
                <td style={tdStyle("right", true)}>{fmt(r.price, 4)}</td>
                <td style={tdStyle("right", true)}>{fmtCur(r.gross_amount, r.currency)}</td>
                <td style={tdStyle("right", true)}>{fmtCur(r.total_fees, r.currency)}</td>
                <td style={{ ...tdStyle("right", true), fontWeight: 700 }}>
                  {fmtCur(r.net_amount, r.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
